<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';
import { INBOX_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import {
  SIDEBAR_PAGE_IDS,
  buildNotificationsDefaultSidebarPageKey,
  isSidebarPageReady,
  markSidebarPageReady,
} from 'dashboard/helper/sidebarPagePreload';

import InboxCard from 'dashboard/components-next/Inbox/InboxCard.vue';
import InboxListHeader from './components/InboxListHeader.vue';
import InboxListControls from './components/InboxListControls.vue';
import AceleresPortfolioTransferRequestsPanel from './components/AceleresPortfolioTransferRequestsPanel.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const notificationList = ref(null);
const page = ref(1);
const status = ref('');
const type = ref('');
const sortOrder = ref(wootConstants.INBOX_SORT_BY.NEWEST);
const readFilter = ref('unread');
const searchQuery = ref('');
const isInboxContextMenuOpen = ref(false);
const INBOX_LIST_WIDTH_KEY = 'acl_inbox_list_width';
const INBOX_LIST_WIDTH_DEFAULT = 497;
const INBOX_LIST_WIDTH_MIN = 320;
const INBOX_LIST_WIDTH_MAX = 900;

const infiniteLoaderOptions = computed(() => ({
  root: notificationList.value,
  rootMargin: '100px 0px 100px 0px',
}));

const meta = useMapGetter('notifications/getMeta');
const uiFlags = useMapGetter('notifications/getUIFlags');
const records = useMapGetter('notifications/getFilteredNotificationsV4');
const inboxById = useMapGetter('inboxes/getInboxById');

const currentConversationId = computed(() => Number(route.params.id));

const readStoredInboxListWidth = () => {
  try {
    const raw = parseInt(localStorage.getItem(INBOX_LIST_WIDTH_KEY) || '', 10);
    if (
      Number.isFinite(raw) &&
      raw >= INBOX_LIST_WIDTH_MIN &&
      raw <= INBOX_LIST_WIDTH_MAX
    ) {
      return raw;
    }
  } catch {
    // Ignore storage access failures.
  }
  return INBOX_LIST_WIDTH_DEFAULT;
};

const inboxListWidth = ref(readStoredInboxListWidth());
const isInboxListResizing = ref(false);
let inboxListDragStartX = 0;
let inboxListDragStartWidth = 0;

const onInboxListResizeMove = event => {
  const width = Math.min(
    INBOX_LIST_WIDTH_MAX,
    Math.max(
      INBOX_LIST_WIDTH_MIN,
      inboxListDragStartWidth + event.clientX - inboxListDragStartX
    )
  );
  inboxListWidth.value = width;
};

const onInboxListResizeEnd = () => {
  isInboxListResizing.value = false;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  window.removeEventListener('mousemove', onInboxListResizeMove);
  window.removeEventListener('mouseup', onInboxListResizeEnd);
  try {
    localStorage.setItem(INBOX_LIST_WIDTH_KEY, String(inboxListWidth.value));
  } catch {
    // Ignore storage access failures.
  }
};

const onInboxListResizeStart = event => {
  isInboxListResizing.value = true;
  inboxListDragStartX = event.clientX;
  inboxListDragStartWidth = inboxListWidth.value;
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  window.addEventListener('mousemove', onInboxListResizeMove);
  window.addEventListener('mouseup', onInboxListResizeEnd);
};

const onInboxListResizeReset = () => {
  inboxListWidth.value = INBOX_LIST_WIDTH_DEFAULT;
  try {
    localStorage.setItem(
      INBOX_LIST_WIDTH_KEY,
      String(INBOX_LIST_WIDTH_DEFAULT)
    );
  } catch {
    // Ignore storage access failures.
  }
};

const inboxFilters = computed(() => ({
  page: page.value,
  status: status.value,
  type: type.value,
  sortOrder: sortOrder.value,
}));

const notifications = computed(() => {
  const loadedNotifications = records.value(inboxFilters.value);
  const normalizedQuery = searchQuery.value.trim().toLowerCase();

  return loadedNotifications.filter(notificationItem => {
    const isRead = Boolean(notificationItem.readAt);
    if (readFilter.value === 'read' && !isRead) return false;
    if (readFilter.value === 'unread' && isRead) return false;
    if (!normalizedQuery) return true;

    const primaryActor = notificationItem.primaryActor || {};
    const sender = primaryActor.meta?.sender || {};
    const searchableText = [
      notificationItem.pushMessageTitle,
      notificationItem.pushMessageBody,
      primaryActor.displayId,
      primaryActor.id,
      sender.name,
      sender.email,
      sender.phoneNumber,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(normalizedQuery);
  });
});

const inboxSidebarCacheKey = computed(() =>
  buildNotificationsDefaultSidebarPageKey()
);

const isDefaultInboxSidebarView = computed(() => {
  return (
    page.value === 1 &&
    !status.value &&
    !type.value &&
    sortOrder.value === wootConstants.INBOX_SORT_BY.NEWEST
  );
});

const showEndOfList = computed(() => {
  return uiFlags.value.isAllNotificationsLoaded && !uiFlags.value.isFetching;
});

const showEmptyState = computed(() => {
  return !uiFlags.value.isFetching && !notifications.value.length;
});

const stateInbox = inboxId => {
  return inboxById.value(inboxId);
};

const fetchNotifications = () => {
  page.value = 1;
  store.dispatch('notifications/clear');
  const filter = inboxFilters.value;
  store.dispatch('notifications/index', filter).then(() => {
    if (isDefaultInboxSidebarView.value) {
      markSidebarPageReady(
        SIDEBAR_PAGE_IDS.NOTIFICATIONS_DEFAULT,
        inboxSidebarCacheKey.value
      );
    }
  });
};

const scrollActiveIntoView = () => {
  const activeEl = notificationList.value?.querySelector('.inbox-card.active');
  activeEl?.scrollIntoView({ block: 'center', behavior: 'smooth' });
};

const redirectToInbox = () => {
  if (route.name === 'inbox_view') return;
  router.replace({ name: 'inbox_view' });
};

const loadMoreNotifications = () => {
  if (uiFlags.value.isAllNotificationsLoaded) return;

  page.value += 1;
  store.dispatch('notifications/index', {
    page: page.value,
    status: status.value,
    type: type.value,
    sortOrder: sortOrder.value,
  });
};

const markNotificationAsRead = async notificationItem => {
  useTrack(INBOX_EVENTS.MARK_NOTIFICATION_AS_READ);

  const { id, primaryActorId, primaryActorType } = notificationItem;
  try {
    await store.dispatch('notifications/read', {
      id,
      primaryActorId,
      primaryActorType,
      unreadCount: meta.value.unreadCount,
    });

    useAlert(t('INBOX.ALERTS.MARK_AS_READ'));
    store.dispatch('notifications/unReadCount');
  } catch {
    // error
  }
};

const markNotificationAsUnRead = async notificationItem => {
  useTrack(INBOX_EVENTS.MARK_NOTIFICATION_AS_UNREAD);
  redirectToInbox();

  const { id } = notificationItem;

  try {
    await store.dispatch('notifications/unread', { id });
    useAlert(t('INBOX.ALERTS.MARK_AS_UNREAD'));
    store.dispatch('notifications/unReadCount');
  } catch {
    // error
  }
};

const deleteNotification = async notificationItem => {
  useTrack(INBOX_EVENTS.DELETE_NOTIFICATION);
  redirectToInbox();

  try {
    await store.dispatch('notifications/delete', {
      notification: notificationItem,
      unread_count: meta.value.unreadCount,
      count: meta.value.count,
    });

    useAlert(t('INBOX.ALERTS.DELETE'));
  } catch {
    // error
  }
};

const onReadFilterChange = nextReadFilter => {
  readFilter.value = nextReadFilter;
  type.value =
    nextReadFilter === 'read' ? wootConstants.INBOX_DISPLAY_BY.READ : '';
  updateUISettings({
    inbox_filter_by: {
      ...uiSettings.value.inbox_filter_by,
      type: type.value,
    },
  });
  fetchNotifications();
};

const onSortOrderChange = nextSortOrder => {
  sortOrder.value = nextSortOrder;
  updateUISettings({
    inbox_filter_by: {
      ...uiSettings.value.inbox_filter_by,
      sort_by: nextSortOrder,
    },
  });
  fetchNotifications();
};

const setSavedFilter = () => {
  const { inbox_filter_by: filterBy = {} } = uiSettings.value;
  const { status: savedStatus, type: savedType, sort_by: sortBy } = filterBy;
  status.value = savedStatus;
  readFilter.value =
    savedType === wootConstants.INBOX_DISPLAY_BY.READ ? 'read' : 'unread';
  type.value = readFilter.value === 'read' ? savedType : '';
  sortOrder.value = sortBy || wootConstants.INBOX_SORT_BY.NEWEST;
  store.dispatch('notifications/setNotificationFilters', inboxFilters.value);
};

const openConversation = async notificationItem => {
  const {
    id,
    primaryActorId,
    primaryActorType,
    primaryActor: { inboxId, id: conversationId },
    notificationType,
  } = notificationItem;

  if (route.params.id === String(conversationId)) return;

  useTrack(INBOX_EVENTS.OPEN_CONVERSATION_VIA_INBOX, {
    notificationType,
  });

  try {
    await store.dispatch('notifications/read', {
      id,
      primaryActorId,
      primaryActorType,
      unreadCount: meta.value.unreadCount,
    });

    // to update the unread count in the store realtime
    store.dispatch('notifications/unReadCount');

    router.push({
      name: 'inbox_view_conversation',
      params: { inboxId, type: 'conversation', id: conversationId },
    });
  } catch {
    // error
  }
};

watch(
  inboxFilters,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      store.dispatch('notifications/updateNotificationFilters', newVal);
    }
  },
  { deep: true }
);

watch(currentConversationId, () => {
  nextTick(scrollActiveIntoView);
});

onMounted(() => {
  scrollActiveIntoView();
  setSavedFilter();
  if (
    isDefaultInboxSidebarView.value &&
    isSidebarPageReady(
      SIDEBAR_PAGE_IDS.NOTIFICATIONS_DEFAULT,
      inboxSidebarCacheKey.value
    )
  ) {
    return;
  }
  fetchNotifications();
});

onUnmounted(() => {
  window.removeEventListener('mousemove', onInboxListResizeMove);
  window.removeEventListener('mouseup', onInboxListResizeEnd);
});
</script>

<template>
  <section class="painel-atendimento flex w-full h-full min-w-0 bg-n-surface-1">
    <div
      class="acl-inbox-list-resizable relative flex h-full w-full flex-col flex-shrink-0 bg-n-surface-1 ltr:border-r rtl:border-l border-n-weak"
      :class="!currentConversationId ? 'flex' : 'hidden xl:flex'"
      :style="{ '--acl-inbox-list-width': `${inboxListWidth}px` }"
    >
      <InboxListHeader
        :is-context-menu-open="isInboxContextMenuOpen"
        @redirect="redirectToInbox"
      />
      <InboxListControls
        v-model:search="searchQuery"
        :read-filter="readFilter"
        :sort-order="sortOrder"
        @update:read-filter="onReadFilterChange"
        @update:sort-order="onSortOrderChange"
      />
      <AceleresPortfolioTransferRequestsPanel />
      <div
        ref="notificationList"
        class="acl-inbox-notifications-list flex min-h-0 flex-1 flex-col gap-0.5 w-full pb-4 overflow-x-hidden px-2 overflow-y-auto divide-y divide-n-weak [&>*:hover]:!border-y-transparent [&>*.active]:!border-y-transparent [&>*:hover+*]:!border-t-transparent [&>*.active+*]:!border-t-transparent"
      >
        <InboxCard
          v-for="notificationItem in notifications"
          :key="notificationItem.id"
          :inbox-item="notificationItem"
          :state-inbox="stateInbox(notificationItem.primaryActor?.inboxId)"
          class="inbox-card rounded-none hover:rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
          :class="
            currentConversationId === notificationItem.primaryActor?.id
              ? 'bg-n-alpha-1 dark:bg-n-alpha-3 !rounded-lg active'
              : ''
          "
          @mark-notification-as-read="markNotificationAsRead"
          @mark-notification-as-un-read="markNotificationAsUnRead"
          @delete-notification="deleteNotification"
          @context-menu-open="isInboxContextMenuOpen = true"
          @context-menu-close="isInboxContextMenuOpen = false"
          @click="openConversation(notificationItem)"
        />
        <div v-if="uiFlags.isFetching" class="flex justify-center my-4">
          <Spinner class="text-n-brand" />
        </div>
        <p
          v-if="showEmptyState"
          class="p-4 text-sm font-medium text-center text-n-slate-10"
        >
          {{ $t('INBOX.LIST.NO_NOTIFICATIONS') }}
        </p>
        <IntersectionObserver
          v-if="!showEndOfList && !uiFlags.isFetching"
          :options="infiniteLoaderOptions"
          @observed="loadMoreNotifications"
        />
      </div>
      <div
        class="acl-inbox-list-resize-handle"
        :class="{ 'acl-inbox-list-resize-handle--active': isInboxListResizing }"
        @mousedown.prevent="onInboxListResizeStart"
        @dblclick="onInboxListResizeReset"
      />
    </div>
    <router-view />
    <CmdBarConversationSnooze />
  </section>
</template>

<style scoped>
.acl-inbox-list-resizable {
  min-height: 0;
  overflow: hidden;
}

.acl-inbox-notifications-list {
  scrollbar-width: thin;
  scrollbar-color: #e5e5e5 transparent;
}

.acl-inbox-notifications-list::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.acl-inbox-notifications-list::-webkit-scrollbar-thumb {
  background-color: #e5e5e5;
  border-radius: 3px;
}

.acl-inbox-notifications-list::-webkit-scrollbar-thumb:hover {
  background-color: #d4d4d4;
}

.acl-inbox-notifications-list::-webkit-scrollbar-track {
  background: transparent;
}

.acl-inbox-list-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  width: 4px;
  cursor: col-resize;
  background: transparent;
  transition: background-color 0.12s;
}

.acl-inbox-list-resize-handle:hover,
.acl-inbox-list-resize-handle--active {
  background: rgba(100, 116, 139, 0.35);
}

@media (min-width: 1280px) {
  .acl-inbox-list-resizable {
    width: var(--acl-inbox-list-width, 497px);
    min-width: 320px;
    max-width: 900px;
  }
}
</style>
